class ReadMoreToggle extends HTMLElement {
  connectedCallback() {
    if (this.dataset.readMoreReady === 'true') {
      return;
    }

    this.dataset.readMoreReady = 'true';

    this.collapsedContent = this.querySelector('.read-more-toggle__collapsed');
    this.summaryContent = this.querySelector('.read-more-toggle__summary');
    this.expandedContent = this.querySelector('.read-more-toggle__expanded');
    this.moreButton = this.querySelector('.read-more-toggle__button--more');
    this.lessButton = this.querySelector('.read-more-toggle__button--less');
    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    const mobileLimit = Number.parseInt(this.dataset.wordLimitMobile || '0', 10);
    this.wordLimit = (isMobile && mobileLimit > 0) ? mobileLimit : Number.parseInt(this.dataset.wordLimit || '32', 10);

    if (!this.moreButton || !this.lessButton || !this.collapsedContent || !this.expandedContent || !this.summaryContent) {
      return;
    }

    this.buildCollapsedContent();
    this.attachInlineButtonTail(this.collapsedContent, this.moreButton, 1, true);
    this.attachInlineButtonTail(this.expandedContent, this.lessButton, 1, false);
    this.moreButton.addEventListener('click', () => this.setExpanded(true));
    this.lessButton.addEventListener('click', () => this.setExpanded(false));
    this.setExpanded(false);
  }

  buildCollapsedContent() {
    const excerptFragment = this.buildExcerptFragment(this.expandedContent, this.wordLimit);

    if (!excerptFragment) {
      return;
    }

    this.collapsedContent.innerHTML = '';
    this.collapsedContent.appendChild(excerptFragment);
  }

  attachInlineButtonTail(container, button, trailingWordCount, addEllipsis) {
    const lastTextNode = this.findLastTextNode(container);

    if (!lastTextNode || !button) {
      return;
    }

    const splitText = this.splitTrailingWords(lastTextNode.textContent || '', trailingWordCount);

    if (!splitText.trailingText) {
      return;
    }

    lastTextNode.textContent = splitText.leadingText;

    const tail = document.createElement('span');
    tail.className = 'read-more-toggle__tail';
    tail.appendChild(document.createTextNode(` ${splitText.trailingText}${addEllipsis ? '...' : ''} `));
    tail.appendChild(button);

    const insertionTarget = lastTextNode.parentNode && lastTextNode.parentNode !== container
      ? lastTextNode.parentNode
      : container;

    insertionTarget.appendChild(tail);
  }

  findLastTextNode(container) {
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
    let lastNode = null;

    while (walker.nextNode()) {
      if (walker.currentNode.textContent.trim()) {
        lastNode = walker.currentNode;
      }
    }

    return lastNode;
  }

  splitTrailingWords(text, trailingWordCount) {
    const matches = text.match(/\S+|\s+/g) || [];
    let wordsFound = 0;
    let splitIndex = matches.length;

    for (let index = matches.length - 1; index >= 0; index -= 1) {
      if (/\S+/.test(matches[index])) {
        wordsFound += 1;
        if (wordsFound === trailingWordCount) {
          splitIndex = index;
          break;
        }
      }
    }

    if (wordsFound < trailingWordCount) {
      return {
        leadingText: text.trimEnd(),
        trailingText: '',
      };
    }

    return {
      leadingText: matches.slice(0, splitIndex).join('').trimEnd(),
      trailingText: matches.slice(splitIndex).join('').trim(),
    };
  }

  buildExcerptFragment(source, wordLimit) {
    const excerptRoot = source.cloneNode(true);
    const state = { wordsRemaining: wordLimit };
    const fragment = document.createDocumentFragment();

    this.trimExcerptNode(excerptRoot, state);

    Array.from(excerptRoot.childNodes).forEach((childNode) => {
      fragment.appendChild(childNode);
    });

    return fragment.childNodes.length ? fragment : null;
  }

  trimExcerptNode(node, state) {
    const childNodes = Array.from(node.childNodes);

    childNodes.forEach((childNode) => {
      if (state.wordsRemaining <= 0) {
        childNode.remove();
        return;
      }

      if (childNode.nodeType === Node.TEXT_NODE) {
        const excerptText = this.truncateTextNode(childNode.textContent || '', state);

        if (!excerptText) {
          childNode.remove();
          return;
        }

        childNode.textContent = excerptText;
        return;
      }

      if (childNode.nodeType !== Node.ELEMENT_NODE) {
        childNode.remove();
        return;
      }

      if (['SCRIPT', 'STYLE'].includes(childNode.tagName)) {
        childNode.remove();
        return;
      }

      if (childNode.tagName === 'BR') {
        childNode.replaceWith(document.createTextNode(' '));
        return;
      }

      this.trimExcerptNode(childNode, state);

      if (!childNode.childNodes.length) {
        childNode.remove();
      }
    });
  }

  truncateTextNode(text, state) {
    if (!text.trim()) {
      return state.wordsRemaining === this.wordLimit ? '' : text;
    }

    let result = '';
    const wordMatches = text.match(/\S+|\s+/g) || [];

    for (const part of wordMatches) {
      if (/\s+/.test(part)) {
        result += part;
        continue;
      }

      if (state.wordsRemaining <= 0) {
        break;
      }

      result += part;
      state.wordsRemaining -= 1;
    }

    return state.wordsRemaining <= 0 ? result.replace(/\s+$/, '') : result;
  }

  setExpanded(isExpanded) {
    this.classList.toggle('is-expanded', isExpanded);
    this.summaryContent.hidden = isExpanded;
    this.expandedContent.hidden = !isExpanded;
    this.moreButton.hidden = isExpanded;
    this.lessButton.hidden = !isExpanded;
    this.moreButton.setAttribute('aria-expanded', String(isExpanded));
    this.lessButton.setAttribute('aria-expanded', String(isExpanded));
  }
}

if (!customElements.get('read-more-toggle')) {
  customElements.define('read-more-toggle', ReadMoreToggle);
}